import React,{useState,useEffect} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Modal from './Modal';
import {useTheme} from '../../contexts/ThemeContext';
import {useBookmarks} from '../../contexts/BookmarkContext';
import {useAuth} from '../../contexts/AuthContext';
import * as dataService from '../../dataService.js';

const {FiLoader}=FiIcons;

const AddFolderModal=({isOpen,onClose,editFolder=null,defaultParentId=null})=> {
  const [formData,setFormData]=useState({name: '',parent: null,sortOrder: 0});
  const [isSubmitting,setIsSubmitting]=useState(false);
  const [isLoading,setIsLoading]=useState(false);
  const [error,setError]=useState(null);
  const {theme}=useTheme();
  const {folders,addFolder,updateFolder}=useBookmarks();
  const {user,isAuthenticated}=useAuth();

  const resetForm=()=> {
    setFormData({name: '',parent: defaultParentId || null,sortOrder: 0});
    setIsLoading(false);
    setError(null);
  };

  useEffect(()=> {
    if (isOpen) {
      if (editFolder) loadFolderData(editFolder.id);
      else resetForm();
    } else {
      resetForm();
    }
  },[isOpen,editFolder,defaultParentId]);

  useEffect(()=> {
    if (isOpen && !isAuthenticated) setError('You must be logged in to manage folders');
  },[isOpen,isAuthenticated]);

  const loadFolderData=async (folderId)=> {
    setIsLoading(true);
    setError(null);
    try {
      const folderData=await dataService.getFolder(folderId);
      setFormData({
        name: folderData.name || '',
        parent: folderData.parent || null,
        sortOrder: Number.isFinite(folderData.sortOrder) ? folderData.sortOrder : 0
      });
    } catch (loadError) {
      setError(`Couldn't load folder: ${loadError.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit=async (event)=> {
    event.preventDefault();
    if (!isAuthenticated || !user) {
      setError('You must be logged in to save folders');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const folderData={
        name: formData.name.trim(),
        parent: formData.parent,
        sortOrder: Math.min(10,Math.max(0,Number(formData.sortOrder) || 0))
      };
      if (editFolder) await updateFolder(editFolder.id,folderData);
      else await addFolder(folderData);
      onClose();
      resetForm();
    } catch (saveError) {
      setError(saveError.message || 'Failed to save folder');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAvailableParentFolders=()=> {
    if (!editFolder) return folders;
    return folders.filter(folder=> {
      if (folder.id===editFolder.id) return false;
      let currentParent=folder.parent;
      while (currentParent) {
        if (currentParent===editFolder.id) return false;
        const parentFolder=folders.find(candidate=> candidate.id===currentParent);
        currentParent=parentFolder?.parent;
      }
      return true;
    });
  };

  if (!isAuthenticated) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editFolder ? 'Edit Folder' : 'Add Folder'}>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <SafeIcon icon={FiLoader} className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">{error}</div>}

          <div>
            <label className="block text-sm font-medium mb-2">Folder Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(event)=> setFormData(previous=> ({...previous,name: event.target.value}))}
              className={`w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme==='dark' ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="Enter folder name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Parent Folder</label>
            <select
              value={formData.parent || ''}
              onChange={(event)=> setFormData(previous=> ({...previous,parent: event.target.value || null}))}
              className={`w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme==='dark' ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="">Root folder</option>
              {[...getAvailableParentFolders()].sort((a,b)=> String(a.name || '').localeCompare(String(b.name || ''))).map(folder=> <option key={folder.id} value={folder.id}>{folder.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Sort Order</label>
            <input
              type="number"
              min="0"
              max="10"
              value={formData.sortOrder}
              onChange={(event)=> setFormData(previous=> ({...previous,sortOrder: Math.min(10,Math.max(0,parseInt(event.target.value,10) || 0))}))}
              className={`w-full px-3 py-2 text-base border rounded-lg ${theme==='dark' ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
            />
            <p className="mt-1 text-xs text-gray-500">PocketBase currently permits folder sort values from 0 to 10.</p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="min-h-11 px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="min-h-11 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {isSubmitting ? 'Saving...' : editFolder ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default AddFolderModal;